// Pallet desk, screen 1 of 2 — the pallet "pack" screen, built on the
// dispatch pack screen's parts so the two desks look and work alike:
// order header (channel badge, carrier, scanned count, weight), the
// address, a progress bar, one row per pallet that turns green when its
// label is scanned, the order's attachments, and the sticky bottom bar
// with the one big button.
//
//   API carrier (Palletways): the row keeps the pallet type and weight
//   editable — they are the booking's inputs. The big button is "Book and
//   print labels"; the page books on the last scan by itself when nothing
//   needs correcting (the button is for a retry or after a correction).
//   Manual (DB Schenker) or direct (JAS) carrier: the row shows height and
//   volumetric weight instead. The big button is "All pallets scanned",
//   which moves on to the confirm screen where the label and Ship live.
import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle, Package, Truck } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { playError } from "../helpers/audioPlayer";
import PalletAddress from "./pallet-address";
import PalletAttachments from "./pallet-attachments";
import PalletCarrierChooser from "./pallet-carrier-chooser";
import {
    PALLETWAYS_TYPES, palletwaysTypeLabel, palletwaysTypeTitle, carrierOptionsOf, isShipOrder, noCarrierKeyOf, routedRuleName,
    type DeskDelivery, type DeskOrder, type DeskState, type Overrides,
} from "./palletDesk";

/** What the last scan did, for the row flash / list shake. `n` changes on
 *  every scan so the same pallet twice still animates. */
export interface ScanFx { n: number; sku: string | null; ok: boolean }

interface Props {
    order: DeskOrder;
    state: DeskState;
    scanned: Set<string>;
    overrides: Overrides;
    onOverride: (sku: string, patch: { type?: string; weight_kg?: number }) => void;
    scanFx: ScanFx;
    busy: boolean;
    readOnly: boolean;
    /** The big button: book (API) or move on to the confirm screen (manual). */
    onPrimary: () => void;
    /** The carrier the operator picked (several can serve the country); the order already carries it as `carrier`. */
    onCarrierChange: (code: string) => void;
    onLeave: () => void;
    /** The delivery address was corrected on the desk (saved already). */
    onDelivery: (delivery: DeskDelivery) => void;
    run: (action: () => Promise<void>) => void;
    fail: (code: string) => void;
    onChanged: () => Promise<void>;
}

const PalletPackScreen = ({ order, state, scanned, overrides, onOverride, scanFx, busy, readOnly, onPrimary, onCarrierChange, onLeave, onDelivery, run, fail, onChanged }: Props) => {
    const { t } = useTranslation();
    // Ships from the confirm screen (manual or direct carrier) rather than
    // booking here: no Palletways type / weight editing, no booking checks.
    const manual = isShipOrder(order);
    const total = order.pallets.length;
    const done = order.pallets.filter((p) => scanned.has(p.sku)).length;
    const allScanned = done === total;

    // Flash the scanned row green; shake the list on a refused scan.
    const [flashSku, setFlashSku] = useState<string | null>(null);
    const [shake, setShake] = useState(false);
    useEffect(() => {
        if (scanFx.n === 0) return;
        if (scanFx.ok && scanFx.sku) {
            setFlashSku(scanFx.sku);
            const timer = window.setTimeout(() => setFlashSku(null), 700);
            return () => window.clearTimeout(timer);
        }
        setShake(true);
        const timer = window.setTimeout(() => setShake(false), 450);
        return () => window.clearTimeout(timer);
    }, [scanFx]);

    const noCarrier = order.carrier === null;
    const editable = state === "ready" && !busy && !readOnly && !manual;
    // Blocked, not disabled: the click explains why, as the dispatch pack
    // screen's Complete button does.
    const canProceed = allScanned && !busy && !readOnly && !noCarrier && state !== "booking";
    const primaryLabel = manual ? t("pallet_dispatch_all_scanned") : t("pallet_dispatch_book");
    const onPrimaryClick = () => {
        if (canProceed) { onPrimary(); return; }
        if (busy) return;
        playError();
        toast.dismiss();
        if (noCarrier) {
            toast.error(t(noCarrierKeyOf(order), { country: order.delivery.country ?? "—" }), { toastId: "pallet-pack-blocked" });
        } else if (!allScanned) {
            toast.error(t(manual ? "pallet_dispatch_scan_all_manual" : "pallet_dispatch_scan_all", { done: String(done), total: String(total) }), { toastId: "pallet-pack-blocked" });
        }
    };

    return (
        <div className="dispatch-page pallet-screen">
            <div className="page-data page-data--full" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
                <div className="page-title-bar">
                    <h1 className="page-title">{t("order_title_label")} #{order.order_number ?? order.order_id}</h1>
                    <button type="button" className="page-back-btn" onClick={onLeave} aria-label={t("back")}>
                        <ArrowLeft size={20} />
                    </button>
                </div>

                <div className="dispatch-order-header">
                    {order.sales_channel && <span className="dispatch-platform-badge">{order.sales_channel}</span>}
                    {order.carrier && (
                        <span className="dispatch-ship-badge dispatch-ship-badge--standard pallet-screen-carrier">
                            <Truck size={16} strokeWidth={2.4} aria-hidden="true" />
                            {order.carrier.name}
                        </span>
                    )}
                    <div className="dispatch-order-stats">
                        <span className={`dispatch-order-stat dispatch-order-stat--count ${allScanned ? "dispatch-order-stat--count-done" : ""}`}>
                            <Package size={20} />
                            <strong>{done}</strong>/{total}
                        </span>
                        <span className="dispatch-order-stat">
                            {t("weight_label")}: <strong>{order.total_kg} kg</strong>
                        </span>
                    </div>
                </div>

                {routedRuleName(order) && (
                    <p className="pallet-dispatch-note pallet-dispatch-rule-line">{t("pallet_dispatch_routed_by_rule", { rule: routedRuleName(order) ?? "" })}</p>
                )}
                {state === "ready" && (
                    <PalletCarrierChooser options={carrierOptionsOf(order)} value={order.carrier?.code ?? null} onChange={onCarrierChange} disabled={busy || readOnly} />
                )}

                <PalletAddress order={order} editable={state === "ready" && !readOnly} busy={busy} run={run} onDelivery={onDelivery} onChanged={onChanged} />

                <div className="dispatch-progress-bar">
                    <div className="dispatch-progress-fill" style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }} />
                </div>

                <div className={`dispatch-items-list pallet-screen-rows${shake ? " pallet-screen-rows--shake" : ""}`}>
                    {order.pallets.map((p) => {
                        const o = overrides[p.sku] ?? {};
                        const isScanned = scanned.has(p.sku);
                        const cls = ["dispatch-item-row", "pallet-screen-row",
                            isScanned ? "dispatch-item-row--scanned" : "",
                            flashSku === p.sku ? "dispatch-item-row--flash-green" : ""].filter(Boolean).join(" ");
                        return (
                            <div key={p.sku} className={cls} data-testid={`pallet-row-${p.sku}`} data-scanned={isScanned ? "true" : undefined}>
                                <div className="dispatch-item-info">
                                    <p className="dispatch-item-title">{p.title || p.sku}</p>
                                    <p className="dispatch-item-sku">
                                        {p.sku}
                                        {p.size_name && <span className="dispatch-item-box-tag">{p.size_name}</span>}
                                    </p>
                                </div>
                                {manual ? (
                                    <dl className="pallet-screen-fields">
                                        <div>
                                            <dt>{t("pallet_dispatch_col_height")}</dt>
                                            <dd>{p.height_cm != null ? `${p.height_cm} cm` : <small className="pallet-dispatch-defaulted">{t("pallet_dispatch_no_height")}</small>}</dd>
                                        </div>
                                        <div>
                                            <dt>{t("pallet_dispatch_col_volumetric")}</dt>
                                            <dd>{p.volumetric_kg != null ? `${p.volumetric_kg} kg` : "—"}</dd>
                                        </div>
                                    </dl>
                                ) : (
                                    <div className="pallet-screen-fields">
                                        <label>
                                            <span>{t("pallet_dispatch_col_type")}</span>
                                            <select
                                                aria-label={`${t("pallet_dispatch_col_type")} ${p.sku}`}
                                                value={o.type ?? p.type ?? ""}
                                                disabled={!editable}
                                                onChange={(e) => onOverride(p.sku, { type: e.target.value })}
                                            >
                                                {!(o.type ?? p.type) && <option value="">{t("pallet_dispatch_choose_type")}</option>}
                                                {PALLETWAYS_TYPES.map((code) => <option key={code} value={code} title={palletwaysTypeTitle(code, t)}>{palletwaysTypeLabel(code, t)}</option>)}
                                            </select>
                                        </label>
                                        <label>
                                            <span>{t("pallet_dispatch_col_weight")}</span>
                                            <input
                                                type="number" min={1} step={1}
                                                aria-label={`${t("pallet_dispatch_col_weight")} ${p.sku}`}
                                                value={o.weight_kg ?? p.weight_kg}
                                                disabled={!editable}
                                                onChange={(e) => {
                                                    const n = Math.round(Number(e.target.value));
                                                    if (Number.isFinite(n) && n > 0) onOverride(p.sku, { weight_kg: n });
                                                }}
                                            />
                                            {p.weight_defaulted && o.weight_kg === undefined && (
                                                <small className="pallet-dispatch-defaulted">{t("pallet_dispatch_weight_default")}</small>
                                            )}
                                        </label>
                                    </div>
                                )}
                                <div className="dispatch-item-status">
                                    {isScanned
                                        ? <CheckCircle size={26} className="pallet-screen-tick" aria-label={t("pallet_dispatch_scanned")} />
                                        : <span className="pallet-screen-pending" aria-hidden="true" />}
                                </div>
                            </div>
                        );
                    })}

                    {/* Palletways' booking checks — never for a manual carrier,
                        whose order is booked on its own site. */}
                    {!manual && state === "ready" && Object.keys(overrides).length === 0 && order.problems.map((pr) => (
                        <p key={`${pr.code}-${pr.sku ?? ""}`} className="pallet-dispatch-note pallet-dispatch-note--warn">{pr.message}</p>
                    ))}
                    {order.booking?.state === "failed" && order.booking.error && (
                        <p className="pallet-dispatch-note pallet-dispatch-note--warn">{t("pallet_dispatch_last_attempt", { error: order.booking.error })}</p>
                    )}
                    {noCarrier && (
                        <p className="pallet-dispatch-note pallet-dispatch-note--warn">
                            {t(noCarrierKeyOf(order), { country: order.delivery.country ?? "—" })}
                        </p>
                    )}
                    {!allScanned && !noCarrier && (
                        <p className="pallet-dispatch-note">
                            {t(manual ? "pallet_dispatch_scan_all_manual" : "pallet_dispatch_scan_all", { done: String(done), total: String(total) })}
                        </p>
                    )}

                    <PalletAttachments order={order} busy={busy} readOnly={readOnly} run={run} fail={fail} onChanged={onChanged} />
                </div>
            </div>

            <div className="dispatch-box-bar pallet-screen-bar">
                <span className="pallet-screen-bar-status">
                    {t("pallet_dispatch_bar_progress", { done: String(done), total: String(total) })}
                </span>
                <button
                    type="button"
                    className={`btn btn-success dispatch-complete-btn ${canProceed ? "dispatch-complete-btn--ready" : "dispatch-complete-btn--blocked"}`}
                    aria-disabled={!canProceed}
                    onClick={onPrimaryClick}
                >
                    {primaryLabel}
                </button>
            </div>
        </div>
    );
};

export default PalletPackScreen;
